import React, { useEffect } from 'react'
import { SideDrawerForm } from '@sales-center/components'
import { Field, FormikErrors, useFormikContext } from 'formik'
import {
  CustomHeaderValue,
  SalesCenterWebhookModel,
  selectSCWebHookFilters,
} from '@user/store/scWebHooks'
import { DebouncedTextField, MuiCheckboxFormik } from '@user/components/Formik'
import WebhookTypeSelect from '@user/components/Settings/SCWebhooks/SCWebhookSideSheet/WebhookTypeSelect/WebhookTypeSelect'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import FormHelperText from '@mui/material/FormHelperText'
import { useSelector } from 'react-redux'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'

export interface Props {
  handleClose: () => void
}

export const SCWebhookSideSheetForm = (props: Props) => {
  const { handleClose } = props

  const {
    submitForm,
    values: { customHeaderValues, isEnabled },
    setFieldValue,
    errors,
    submitCount,
    setValues,
  } = useFormikContext<SalesCenterWebhookModel>()
  const { selectedWebHook } = useSelector(selectSCWebHookFilters)
  const tidalThemeActive = useTidalThemeActive()
  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'

  const customHeaderErrors = errors.customHeaderValues as FormikErrors<{
    customHeaderValues: CustomHeaderValue[]
  }>

  useEffect(() => {
    if (selectedWebHook) {
      setValues({
        id: selectedWebHook.id,
        webhookType: selectedWebHook.webhookType,
        isEnabled: selectedWebHook.isEnabled,
        entityType: {
          lead: ['Any', 'Lead'].includes(selectedWebHook.entityType),
          opportunity: ['Any', 'Opportunity'].includes(
            selectedWebHook.entityType,
          ),
        },
        endpointUrl: selectedWebHook.endpointUrl,
        customHeaderValues: selectedWebHook.customHeaderValues.map(
          (header) => ({
            ...header,
            errors: '',
          }),
        ),
        email: selectedWebHook.email,
      })
    }
  }, [selectedWebHook, setValues])

  return (
    <SideDrawerForm
      tidalMode={true}
      onSave={submitForm}
      onCancel={handleClose}
      secondaryBtnLabel="Cancel"
      padding="0 20px"
      primaryBtnLabel="Save"
      containerStyle={{
        py: '15px',
        px: 2,
        flexDirection: 'column',
        display: 'flex',
      }}
      actionButtonHeight="36px"
    >
      {selectedWebHook && (
        <>
          <Typography variant="subtitle3" mb={1}>
            Status
          </Typography>
          <FormControlLabel
            sx={{
              fontWeight: tidalThemeActive ? 'bold' : '',
              mb: '32px',
              ml: 0,
              gap: (theme) => theme.spacing(0.8),
            }}
            labelPlacement="end"
            label={isEnabled ? 'Active' : 'Inactive'}
            control={
              <Switch
                checked={isEnabled}
                onChange={() => {
                  setFieldValue('isEnabled', !isEnabled)
                }}
              />
            }
          />
        </>
      )}

      <Field
        name="endpointUrl"
        label="Webhook URL"
        component={DebouncedTextField}
        required
        inputProps={{ maxLength: 250 }}
        avoidValidateOnBlur
        size="medium"
      />

      <WebhookTypeSelect
        name="webhookType"
        label="Webhook Type"
        disabled={true}
        density={density}
        required={true}
      />

      <Typography variant="subtitle3" mb={1}>
        Select Record Type(s)*
      </Typography>

      <Field
        component={MuiCheckboxFormik}
        label="Lead"
        name="entityType.lead"
        size="medium"
        color="primary"
        margin={0}
      />

      <Field
        component={MuiCheckboxFormik}
        label="Opportunity"
        name="entityType.opportunity"
        size="medium"
        color="primary"
        margin={0}
      />

      {submitCount > 0 && errors.entityType?.lead !== '' && (
        <FormHelperText sx={{ ml: 1 }} error>
          {errors.entityType?.lead}
        </FormHelperText>
      )}

      <Typography variant="subtitle3" mt="32px" mb={2}>
        Notify on Failure
      </Typography>

      <Field
        name="email"
        label="Email Address"
        component={DebouncedTextField}
        inputProps={{ maxLength: 200 }}
        avoidValidateOnBlur
        size="medium"
        sx={{ mb: '32px' }}
      />

      <Stack mb={2} flexDirection="row" justifyContent="space-between">
        <Typography variant="subtitle3">Custom Header Value</Typography>

        <Button
          variant="text"
          size="small"
          onClick={() =>
            setFieldValue('customHeaderValues', [
              ...customHeaderValues,
              {
                key: '',
                value: '',
                error: '',
              },
            ])
          }
        >
          Add Custom Header Value
        </Button>
      </Stack>

      {customHeaderValues.map((_item, index) => {
        return (
          <Stack key={`custom-header-${index}`} mb={1}>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              gap="16px"
              alignItems="center"
            >
              <Field
                name={`customHeaderValues[${index}].key`}
                label="Key"
                component={DebouncedTextField}
                inputProps={{ maxLength: 25 }}
                avoidValidateOnBlur
                size="medium"
                error={false}
              />
              <Field
                name={`customHeaderValues[${index}].value`}
                label="Value"
                component={DebouncedTextField}
                inputProps={{ maxLength: 1000 }}
                avoidValidateOnBlur
                size="medium"
                helperText=""
              />
              {customHeaderValues.length > 1 && (
                <IconButton
                  onClick={() => {
                    const newValues = customHeaderValues.filter(
                      (_, i) => i !== index,
                    )
                    setFieldValue('customHeaderValues', newValues)
                  }}
                  sx={{
                    color: (theme) => theme.palette.action.disabled,
                  }}
                  size="medium"
                  data-test-id={`webhookCustomHeaderRemoveBtn-${index}`}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Stack>

            {submitCount > 0 && customHeaderErrors?.[index]?.error && (
              <FormHelperText sx={{ ml: 1 }} error>
                {customHeaderErrors[index].error}
              </FormHelperText>
            )}
          </Stack>
        )
      })}
    </SideDrawerForm>
  )
}
